import { gameState } from '../state/gameState.js';
import { SCENE_TO_BACKEND } from '../config.js';
import { applyPlacementSize } from './placementSizing.js';
import { perspectiveBox, placementProfile } from './placementPerspective.mjs';
import { messageFor } from '../api/errors.js';
import { showToast } from './Toast.js';
import { clampPlacementY } from '../scenes/floors.js';
import { virtualInput } from './virtualInput.js';
import { settleToast } from './animalCopy.js';
import { isTouchDevice } from './isTouchDevice.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';

class PlaceMode {
  constructor() {
    this.active = false;
    this.scene = null;
    this.preview = null;
    this.item = null;
    this.type = null;
    this.mode = 'place';
    this.placementId = null;
  }

  enterPlace(scene, type, item) {
    if (this.active) this.exit();
    this.active = true; this.scene = scene; this.type = type; this.item = item; this.mode = 'place';
    this._beginPreview(`${type}_${item}`);
  }

  // flipped: the piece's "Odwróć" — its ghost is drawn the way it stands (moving keeps it).
  enterMove(scene, placementId, type, item, flipped = false) {
    if (this.active) this.exit();
    this.active = true; this.scene = scene; this.type = type; this.item = item;
    this.mode = 'move'; this.placementId = placementId;
    this._beginPreview(`${type}_${item}`, flipped);
  }

  _beginPreview(textureKey, flipped = false) {
    if (!this.scene.textures.exists(textureKey)) {
      showToast(this.scene, 'Brak grafiki dla tego itemu');
      this.exit();
      return;
    }
    this.preview = this.scene.add.image(0, 0, textureKey).setAlpha(0.5).setDepth(9999).setFlipX(flipped);
    applyPlacementSize(this.preview, this.item);
    // Full size: the floor rule (and the server) work on it; the ghost is drawn at the room's perspective (_move).
    this._baseW = this.preview.displayWidth;
    this._baseH = this.preview.displayHeight;
    // The click that selected "Przesuń"/"Postaw" fires on pointerup; Phaser dispatches the
    // scene-level pointerup right after the button's, in the same release. Without arming we'd
    // commit instantly under the cursor. Require a fresh pointerdown after entering this mode.
    this._armed = false;
    this._move = this._move.bind(this);
    this._down = this._down.bind(this);
    this._click = this._click.bind(this);
    this._cancel = this._cancel.bind(this);
    this.scene.input.on('pointermove', this._move);
    this.scene.input.on('pointerdown', this._down);
    this.scene.input.on('pointerup', this._click);
    this.scene.input.keyboard.on('keydown-ESC', this._cancel);
    virtualInput.registerCancel(() => this._cancel());
    // Phones have no hover, so a tap used to place the item unseen wherever it landed. On touch
    // the ghost shows at once mid-room, the finger drags it, and the action button places it.
    this._touch = isTouchDevice();
    if (this._touch) {
      const cam = this.scene.cameras.main;
      this._move({ worldX: cam.scrollX + GAME_WIDTH / 2, worldY: cam.scrollY + GAME_HEIGHT * 0.62 });
      virtualInput.registerAction(this.mode === 'move' ? 'Przesuń tutaj' : 'Postaw tutaj',
        () => this._commit(this.preview.x, this._targetY));
    }
  }

  _move(p) {
    if (!this.preview) return;
    const sceneKey = this.scene.scene.key;
    const backendScene = SCENE_TO_BACKEND[sceneKey];
    // Floor rule: floor items never rise above the wall/floor line (server enforces the same).
    const y = clampPlacementY(backendScene, this.type, this.item, p.worldX, p.worldY, this._baseH);
    // Drawn the size the piece will have there: smaller towards the back wall, its bottom edge where it will stand.
    const b = perspectiveBox({ x: p.worldX, y, w: this._baseW, h: this._baseH },
      placementProfile({ type: this.type, item: this.item, scene: backendScene }, this.scene.perspective));
    this.preview.setDisplaySize(b.w, b.h).setPosition(b.x, b.y);
    this._targetY = p.worldY;
  }

  _down(p) {
    this._armed = true;
    if (this._touch && p) this._move(p);   // the ghost jumps to the finger, then follows the drag
  }

  async _click(p) {
    if (p.button !== 0) return;
    if (!this._armed) return;  // ignore the pointerup that opened this mode
    if (this._touch) { this._move(p); return; }   // phones confirm with the action button
    await this._commit(p.worldX, p.worldY);
  }

  async _commit(worldX, worldY) {
    const p = { worldX, worldY };
    const sceneKey = this.scene.scene.key;
    const backendScene = SCENE_TO_BACKEND[sceneKey];
    if (!backendScene) {
      showToast(this.scene, 'W tej scenie nie można niczego postawić');
      this.exit();
      return;
    }
    const clampY = clampPlacementY(backendScene, this.type, this.item, p.worldX, p.worldY, this._baseH ?? 0);
    try {
      if (this.mode === 'place') {
        const pocketBefore = { ...(gameState.raw?.inventory?.unplaced_animals ?? {}) };
        await gameState.placeObject(this.type, this.item, backendScene, Math.round(p.worldX), Math.round(clampY));
        const pocketAfter = gameState.raw?.inventory?.unplaced_animals ?? {};
        // A settled animal is identified by which pocket count went down, not by
        // assuming the last entry in `animals` — placement order isn't guaranteed.
        const settledType = this.type === 'buda'
          ? Object.keys(pocketBefore).find((t) => (pocketBefore[t] ?? 0) > (pocketAfter[t] ?? 0))
          : undefined;
        if (settledType) {
          const name = gameState.raw?.catalog?.animals?.[settledType]?.name_pl ?? settledType;
          showToast(this.scene, settleToast(settledType, name));
        } else {
          showToast(this.scene, 'Postawione! {party}');
        }
      } else {
        await gameState.moveObject(this.placementId, Math.round(p.worldX), Math.round(clampY));
        showToast(this.scene, 'Przesunięte');
      }
    } catch (e) {
      showToast(this.scene, messageFor(e));
    }
    this.exit();
  }

  _cancel() {
    showToast(this.scene, 'Anulowano');
    this.exit();
  }

  exit() {
    if (!this.active) return;
    if (this.preview) { this.preview.destroy(); this.preview = null; }
    if (this.scene) {
      this.scene.input.off('pointermove', this._move);
      this.scene.input.off('pointerdown', this._down);
      this.scene.input.off('pointerup', this._click);
      this.scene.input.keyboard.off('keydown-ESC', this._cancel);
      virtualInput.clearCancel();
      if (this._touch && /tutaj$/.test(virtualInput.getAction()?.label ?? '')) virtualInput.clearAction();   // only our own
    }
    this.active = false; this.scene = null; this.item = null; this.type = null; this.placementId = null;
  }
}

export const placeMode = new PlaceMode();
